import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { usePermissions } from '@store/auth';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ReplayCircleFilled as ReplayCircleFilledIcon,
} from '@mui/icons-material';

import ConfirmationDialog from '@components/ConfirmationDialog';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';

import {
  getContextualMessagesMerchantMapping,
  deleteContextualMessagesMerchantMapping,
  invalidateContextualMessagesMerchantMappingCache,
  selectContextualMessagesMerchantMapping,
  selectIsLoadingContextualMessagesMerchantMapping,
} from '../../store/payment-options-contextual-messages';

import {
  CONTEXTUAL_MESSAGES_NEW_ROUTE,
  viewContextualMessages,
} from '../../payment-options.breadcrumbs';

const MESSAGES = {
  DeleteDialogTitle: 'Delete contextual messages?',
  DeleteDialogDescription:
    'Are you sure you want to delete the selected contextual messages?',
  CacheRefreshSuccess: 'Yay! Cache has been successfully invalidated.',
  CacheRefreshFailure: 'Oops! Something went wrong while invalidating cache.',
  DeleteSuccess: 'Yay! Contextual messages has been deleted successfully.',
  DeleteFailure:
    'Oops! Something went wrong while deleting the contextual messages.',
};

function PropertyKeyToolbar({
  fetchFromDB = false,
  enableDelete = false,
  onFetchFromDB,
  onCacheRefresh,
  onCreateNew,
  onDelete,
  permissions,
}) {
  return (
    <GridToolbarContainer sx={{ p: '12px', justifyContent: 'space-between' }}>
      {enableDelete ? (
        <Button
          disabled={!permissions?.canEditContextualMessages}
          variant="contained"
          onClick={onDelete}
          startIcon={<DeleteIcon />}>
          Delete
        </Button>
      ) : (
        <GridToolbarQuickFilter />
      )}
      <Stack sx={{ flexDirection: 'row', gap: '12px' }}>
        <FormControlLabel
          control={
            <Switch
              checked={fetchFromDB}
              onChange={onFetchFromDB}
            />
          }
          label="Fetch from DB"
        />
        <Button
          disabled={!permissions?.canEditContextualMessages}
          variant="outlined"
          onClick={onCacheRefresh}
          startIcon={<ReplayCircleFilledIcon />}>
          Refresh Cache
        </Button>
        <Button
          disabled={!permissions?.canEditContextualMessages}
          variant="contained"
          onClick={onCreateNew}
          startIcon={<AddIcon />}>
          Add new
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
}

export default function ContextualMessagesList() {
  const dispatch = useDispatch();
  const permissions = usePermissions();
  const navigate = useNavigate();

  const [fetchFromDB, setFetchFromDB] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });

  const enableDelete = selectedRows.length !== 0;

  const contextualMessagesMerchantMapping = useSelector(
    selectContextualMessagesMerchantMapping,
  );
  const isLoadingContextualMessagesMerchantMapping = useSelector(
    selectIsLoadingContextualMessagesMerchantMapping,
  );

  const fetchContextualMessagesMerchantMapping = useCallback(() => {
    dispatch(getContextualMessagesMerchantMapping(fetchFromDB));
  }, [fetchFromDB, dispatch]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewContextualMessages));
  }, [dispatch]);

  useEffect(() => {
    fetchContextualMessagesMerchantMapping();
  }, [fetchContextualMessagesMerchantMapping]);

  const onFetchFromDB = () => {
    setFetchFromDB((prev) => !prev);
  };

  const onCacheRefresh = () => {
    dispatch(invalidateContextualMessagesMerchantMappingCache()).then(
      ({ payload }) => {
        if (payload && payload.status === 'SUCCESS') {
          dispatch(showSuccessMessage(MESSAGES.CacheRefreshSuccess));
        } else {
          dispatch(
            showErrorMessage({
              message: MESSAGES.CacheRefreshFailure,
              duration: 4000,
            }),
          );
        }
      },
    );
  };

  const onCreateNew = () => {
    navigate(CONTEXTUAL_MESSAGES_NEW_ROUTE);
  };

  const onDelete = () => {
    setShowDeleteModal({ show: true });
  };

  const onDeleteConfirmation = (mappingList) => {
    dispatch(deleteContextualMessagesMerchantMapping({ mappingList })).then(
      ({ payload }) => {
        if (payload && payload.status === 'SUCCESS') {
          dispatch(showSuccessMessage(MESSAGES.DeleteSuccess));
          fetchContextualMessagesMerchantMapping();
        } else {
          dispatch(
            showErrorMessage({
              message: MESSAGES.DeleteFailure,
              duration: 4000,
            }),
          );
        }
      },
    );
  };

  const renderCell = (line1, line2) => (
    <Box>
      <Typography
        variant="body1"
        sx={{ whiteSpace: 'break-spaces' }}>
        {line1}
      </Typography>
      {line2 && <Typography variant="caption">({line2})</Typography>}
    </Box>
  );

  return (
    <ApplicationPageTemplate
      title="Contextual Messages"
      pageReady>
      <DataGrid
        getRowHeight={() => 'auto'}
        density="comfortable"
        loading={isLoadingContextualMessagesMerchantMapping}
        rows={contextualMessagesMerchantMapping}
        checkboxSelection
        disableSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSorting
        disableColumnSelector
        disableDensitySelector
        onSelectionModelChange={(row) => {
          const selection = row.map((rowId) => {
            const [merchantId, bannerId] = rowId.split('-'); // RowId = {merchantId}-{bannerId}
            return { merchantId, bannerId };
          });
          setSelectedRows(selection);
        }}
        components={{
          Toolbar: () =>
            PropertyKeyToolbar({
              fetchFromDB,
              enableDelete,
              onFetchFromDB,
              onCacheRefresh,
              onCreateNew,
              onDelete,
              permissions,
            }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        getRowId={({ merchantId, bannerId }) => `${merchantId}-${bannerId}`}
        columns={[
          {
            field: 'bannerName',
            headerName: 'Banner name',
            flex: 2,
            renderCell: ({ row: { bannerName, code } }) =>
              renderCell(bannerName, code),
          },
          {
            field: 'message',
            headerName: 'Banner message',
            sortable: false,
            headerAlign: 'left',
            align: 'left',
            flex: 3,
            renderCell: ({ row: { message } }) => renderCell(message),
          },
          {
            field: 'bannerType',
            headerName: 'Banner Type',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row: { bannerType = 'info' } }) =>
              renderCell(bannerType),
          },
          {
            field: 'overridePossible',
            headerName: 'LOB Override',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row: { overridePossible } }) =>
              renderCell(overridePossible === 1 ? 'Yes' : 'No'),
          },
          {
            field: 'lobName',
            headerName: 'Line of Business',
            flex: 2,
            renderCell: ({ row: { lobName, lobCode } }) =>
              renderCell(lobName, lobCode),
          },
          {
            field: 'platformName',
            headerName: 'Platform',
            flex: 2,
            renderCell: ({ row: { platformName, platformCode } }) =>
              renderCell(platformName, platformCode),
          },
        ]}
      />

      <ConfirmationDialog
        openDialog={showDeleteModal}
        yesHandler={onDeleteConfirmation}
        data={selectedRows}
        title={MESSAGES.DeleteDialogTitle}
        message={MESSAGES.DeleteDialogDescription}
      />
    </ApplicationPageTemplate>
  );
}
